/**
 * Locale-aware display formatting for the pricing calculator. Currency comes
 * from the request locale (see `LOCALE_CURRENCY`); `narrowSymbol` keeps AUD
 * as a bare `$`, matching the rest of the site.
 */

import { parseIsoDate } from "./model";

export interface CalculatorFormatters {
  money: (value: number, fractionDigits?: number) => string;
  compactMoney: (value: number) => string;
  percent: (value: number, fractionDigits?: number) => string;
  date: (isoDate: string) => string;
  monthLabel: (isoDate: string) => string;
  number: (value: number, fractionDigits?: number) => string;
}

export function createFormatters(
  locale: string,
  currency: string
): CalculatorFormatters {
  const moneyCache = new Map<number, Intl.NumberFormat>();
  const moneyFormatter = (fractionDigits: number) => {
    let formatter = moneyCache.get(fractionDigits);
    if (!formatter) {
      formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
      moneyCache.set(fractionDigits, formatter);
    }
    return formatter;
  };

  const compactFormatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    notation: "compact",
    maximumFractionDigits: 1,
  });

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

  const monthFormatter = new Intl.DateTimeFormat(locale, {
    month: "short",
    timeZone: "UTC",
  });

  const numberCache = new Map<number, Intl.NumberFormat>();
  const numberFormatter = (fractionDigits: number) => {
    let formatter = numberCache.get(fractionDigits);
    if (!formatter) {
      formatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
      numberCache.set(fractionDigits, formatter);
    }
    return formatter;
  };

  return {
    money: (value, fractionDigits = 0) =>
      moneyFormatter(fractionDigits).format(value),
    // Site convention is a lowercase compact suffix (k, m, b).
    compactMoney: (value) =>
      compactFormatter
        .format(value)
        .replace(/K\b/, "k")
        .replace(/M\b/, "m")
        .replace(/B\b/, "b"),
    percent: (value, fractionDigits = 2) =>
      `${numberFormatter(fractionDigits).format(value)}%`,
    date: (isoDate) => dateFormatter.format(parseIsoDate(isoDate)),
    monthLabel: (isoDate) => monthFormatter.format(parseIsoDate(isoDate)),
    number: (value, fractionDigits = 0) =>
      numberFormatter(fractionDigits).format(value),
  };
}
